import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFirebaseStorage } from "@/lib/firebase";

/**
 * Compress an image file to reduce size
 * @param file - The image file to compress
 * @param maxSizeMB - Maximum size in MB (default: 1MB)
 * @param maxWidthOrHeight - Maximum width or height in pixels (default: 1920)
 * @returns Compressed file as Blob
 */
async function compressImage(
  file: File,
  maxSizeMB: number = 1,
  maxWidthOrHeight: number = 1920
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidthOrHeight) {
            height = (height * maxWidthOrHeight) / width;
            width = maxWidthOrHeight;
          }
        } else {
          if (height > maxWidthOrHeight) {
            width = (width * maxWidthOrHeight) / height;
            height = maxWidthOrHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels to achieve target size
        let quality = 0.9;
        const targetSizeBytes = maxSizeMB * 1024 * 1024;

        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              if (blob.size <= targetSizeBytes || quality <= 0.5) {
                resolve(blob);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            },
            file.type,
            quality
          );
        };

        tryCompress();
      };
      img.onerror = () => reject(new Error("Failed to load image"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
  });
}

/**
 * Upload a single property image to Firebase Storage
 * @param file - The image file to upload
 * @param propertyId - The property ID (or temp ID for draft)
 * @param userId - The user ID of the property owner
 * @returns Download URL of the uploaded image
 */
export async function uploadPropertyImage(
  file: File,
  propertyId: string,
  userId: string
): Promise<string> {
  try {
    const storage = getFirebaseStorage();
    if (!storage) {
      throw new Error("Firebase Storage is not initialized");
    }

    // Compress image before upload
    const compressedBlob = await compressImage(file);

    // Generate unique filename
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 9);
    const extension = file.name.split(".").pop() || "jpg";
    const fileName = `${timestamp}_${randomStr}.${extension}`;

    // Storage path: properties/{userId}/{propertyId}/images/{fileName}
    const storageRef = ref(
      storage,
      `properties/${userId}/${propertyId}/images/${fileName}`
    );

    // Upload compressed image
    await uploadBytes(storageRef, compressedBlob, {
      contentType: file.type,
    });

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw new Error("Failed to upload image");
  }
}

/**
 * Upload multiple property images
 * @param files - Array of image files
 * @param propertyId - The property ID (or temp ID for draft)
 * @param userId - The user ID of the property owner
 * @param onProgress - Optional callback for upload progress
 * @returns Array of download URLs
 */
export async function uploadPropertyImages(
  files: File[],
  propertyId: string,
  userId: string,
  onProgress?: (current: number, total: number) => void
): Promise<string[]> {
  const urls: string[] = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const url = await uploadPropertyImage(files[i], propertyId, userId);
      urls.push(url);

      if (onProgress) {
        onProgress(i + 1, files.length);
      }
    } catch (error) {
      console.error(`Failed to upload image ${i + 1}:`, error);
      // Continue with other uploads even if one fails
    }
  }

  return urls;
}

/**
 * Delete a property image from Firebase Storage
 * @param imageUrl - The download URL of the image to delete
 */
export async function deletePropertyImage(imageUrl: string): Promise<void> {
  try {
    const storage = getFirebaseStorage();
    if (!storage) {
      throw new Error("Firebase Storage is not initialized");
    }
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
  } catch (error) {
    console.error("Error deleting image:", error);
    throw new Error("Failed to delete image");
  }
}

/**
 * Delete all images for a property
 * @param propertyId - The property ID
 * @param userId - The user ID of the property owner
 * @param imageUrls - Array of image URLs to delete
 */
export async function deletePropertyImages(imageUrls: string[]): Promise<void> {
  const deletePromises = imageUrls.map((url) => deletePropertyImage(url));
  await Promise.allSettled(deletePromises);
}

/**
 * Upload maintenance request images/videos
 * @param userId - The user ID
 * @param requestId - The maintenance request ID
 * @param files - Array of image/video files
 * @returns Array of download URLs
 */
export async function uploadMaintenanceImages(
  userId: string,
  requestId: string,
  files: File[]
): Promise<string[]> {
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error("Firebase Storage is not initialized");
  }

  const urls: string[] = [];

  for (const file of files) {
    try {
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 9);
      const extension = file.name.split(".").pop() || "jpg";
      const fileName = `${timestamp}_${randomStr}.${extension}`;

      // Storage path: maintenance/{userId}/{requestId}/{fileName}
      const storageRef = ref(
        storage,
        `maintenance/${userId}/${requestId}/${fileName}`
      );

      // For images, compress before upload
      let uploadData: Blob = file;
      if (file.type.startsWith("image/")) {
        uploadData = await compressImage(file);
      }

      // Upload file
      await uploadBytes(storageRef, uploadData, {
        contentType: file.type,
      });

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      urls.push(downloadURL);
    } catch (error) {
      console.error(`Failed to upload file:`, error);
      // Continue with other uploads
    }
  }

  return urls;
}
